import { ReactNode, useState } from 'react';
import { useHistory } from 'react-router-dom';

import customComponentRegistry from 'modules/Tasks/SubmitActions/customComponents/registry';
import DownloadModal from 'modules/Tasks/SubmitActions/DownloadModal';
import FormModal from 'modules/Tasks/SubmitActions/FormModal';
import UploadModal from 'modules/Tasks/SubmitActions/UploadModal';
import useDeepLinkAutoOpen from 'modules/common/hooks/useDeepLinkAutoOpen';
import interpolate from 'modules/Tasks/utils/interpolate';
import isOverlaySubmitAction from 'modules/Tasks/utils/isOverlaySubmitAction';
import { SubmitActionTypes, Task } from 'modules/Tasks/types';

/** Owns the click handler and post-click surface for a task's submit_action. */
export default function useTaskSubmitAction(
    task: Task,
    options?: { autoOpen?: boolean }
): {
    onCtaClick: () => void;
    actionSurface: ReactNode;
} {
    const history = useHistory();
    const [isOpen, setIsOpen] = useState(false);
    const close = () => setIsOpen(false);
    const submit = task.task_template.submit_action;

    useDeepLinkAutoOpen(Boolean(options?.autoOpen) && isOverlaySubmitAction(submit), () => setIsOpen(true));

    const onCtaClick = () => {
        if (submit.type === SubmitActionTypes.REDIRECT) {
            const url = interpolate(submit.url, task.context);
            if (submit.is_external) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                history.push(url);
            }
            return;
        }
        setIsOpen(true);
    };

    const actionSurface = (() => {
        switch (submit.type) {
            case SubmitActionTypes.UPLOAD:
                return <UploadModal open={isOpen} onClose={close} task={task} />;
            case SubmitActionTypes.FORM:
                return <FormModal open={isOpen} onClose={close} task={task} />;
            case SubmitActionTypes.DOWNLOAD:
                return <DownloadModal open={isOpen} onClose={close} task={task} />;
            case SubmitActionTypes.CUSTOM: {
                const Component = customComponentRegistry[submit.component_name];
                return Component ? <Component open={isOpen} onClose={close} task={task} /> : null;
            }
            case SubmitActionTypes.REDIRECT:
            default:
                return null;
        }
    })();

    return { onCtaClick, actionSurface };
}
